/**
 * 에디터 → 매뉴얼 컨텍스트 도움말 링크 정본.
 *
 * 에디터의 어느 지점이 어느 문서(+앵커)로 가는지를 **여기 한 곳에만** 둔다. 링크를 JSX에
 * 흩어 놓으면 문서 slug/제목이 바뀔 때 전수 수정이 불가능해지고, 깨진 링크는 도움말이 아니라
 * 방해가 된다. `manualLinks.test.ts`가 이 표의 모든 대상 문서·앵커가 `docs/manual/` 아래에
 * 실제로 존재하는지 검증하므로, 문서가 사라지거나 제목이 바뀌면 CI에서 잡힌다.
 *
 * 경로 규약은 admin 라우트 `manual/:manual/:section/:slug`(App.tsx)와 같다.
 */

/** 도움말 링크 1건 — 이동 대상과 `?` 아이콘의 안내 문구. */
export interface ManualLink {
  /** `/manual/<매뉴얼>/<섹션>/<slug>[#앵커]` */
  href: string;
  /** 무엇을 여는지 한 줄 — title/aria-label에 그대로 쓰인다. */
  label: string;
}

export const MANUAL_LINKS = {
  /** 바인딩(값 연결) 속성 영역 — "이 칸에 어떤 데이터를 넣을까요?" */
  binding: {
    href: '/manual/composer/concepts/c1-two-layers',
    label: '값 연결이란?',
  },
  /** 식별 키 입력 — 같은 키를 여러 칸에 걸면 값이 공유된다. */
  bindingKey: {
    href: '/manual/composer/concepts/c2-binding-key',
    label: '식별 키는 무엇을 묶나요?',
  },
  /** 표시 형식 선택(단위/날짜/치환 등). */
  bindingFormat: {
    href: '/manual/composer/reference/r3-binding-formats',
    label: '표시 형식 전체 보기',
  },
  /** 보조 자료 가져오기 — 형식이 읽을 자료를 이름 붙여 끌어오는 선언(scopeFrom). */
  formatScopeFrom: {
    href: '/manual/composer/how-to/data-format#보조-자료-가져오기',
    label: '보조 자료는 왜 가져오나요?',
  },
  /** 미배치 위젯 풀 — "만들어졌다고 화면에 보이는 건 아닙니다". */
  unplacedPool: {
    href: '/manual/composer/concepts/c1-two-layers#만들어졌다고-화면에-보이는-건-아닙니다',
    label: '미배치 위젯이 뭔가요?',
  },
  /** 작성 페이지 위젯 트리 패널 — 노드(양식)와 위젯(작성)의 차이. */
  widgetTree: {
    href: '/manual/composer/concepts/c5-node-vs-widget',
    label: '노드와 위젯의 차이',
  },
  /** 통합 "데이터" 섹션 — 시스템 조회 조립 / 동기화 데이터 / 직접 입력을 언제 고르는가. */
  dataKinds: {
    href: '/manual/composer/concepts/c3-data-kinds',
    label: '어떤 데이터를 골라야 하나요?',
  },
  /** 요소 팔레트 — 양식 탭에 놓는 노드 종류. */
  nodePalette: {
    href: '/manual/composer/reference/r2-nodes',
    label: '노드 종류 전체 보기',
  },
  /** 위젯 추가 팔레트 — 작성 탭에 놓는 위젯 종류. */
  widgetPalette: {
    href: '/manual/composer/reference/r1-widgets',
    label: '위젯 종류 전체 보기',
  },
  /**
   * 작성 탭 툴바의 「⚙ 위젯 자동 배치」 — 양식 본문 구조를 읽어 위젯을 놓는다(2026-10-04).
   * 버튼 자리에서 생기는 질문이 둘이다: **무엇을 읽는가** / 채우기와 전체 재구성 중 무엇을 고르나.
   * 둘 다 한 문서가 답하므로 앵커 없이 문서로 보낸다.
   */
  writeFormAutoLayout: {
    href: '/manual/composer/how-to/write-form-auto-layout',
    label: '자동 배치는 무엇을 읽나요?',
  },
  /** 반복 표형의 열 헤더 — 별도 설정값이 아니라 열 위젯(라벨·제목·슬롯 이름) 파생. */
  repeatTableHeader: {
    href: '/manual/composer/how-to/repeat-table',
    label: '열 이름은 어디서 오나요?',
  },
  /** 양식 속성의 「분류」 — 분류는 어디서 만들고, 미분류는 무엇인가(2026-09-20). */
  templateCategories: {
    href: '/manual/composer/how-to/template-categories',
    label: '양식을 분류로 묶기',
  },
} as const satisfies Record<string, ManualLink>;

export type ManualLinkKey = keyof typeof MANUAL_LINKS;
